import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { ThemeProvider } from "@/context/ThemeContext";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { SamTipsProvider } from "@/context/SamTipsContext";
import { Toaster } from "@/components/ui/sonner";
import { ErrorBoundary } from "@/components/ErrorBoundary";

import Landing from "@/pages/Landing";
import Pricing from "@/pages/Pricing";
import About from "@/pages/About";
import PromisePage from "@/pages/Promise";
import Playbook from "@/pages/Playbook";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AuthCallback from "@/pages/AuthCallback";
import AppHome from "@/pages/AppHome";
import Settings from "@/pages/Settings";
import ModuleStub from "@/pages/ModuleStub";
import Chat from "@/pages/Chat";
import KnowledgeBase from "@/pages/KnowledgeBase";
import Leads from "@/pages/Leads";
import Sequences, { SequenceBuilder } from "@/pages/Sequences";
import Content from "@/pages/Content";
import Agents, { AgentBuilder } from "@/pages/Agents";
import Widget from "@/pages/Widget";
import Assistant, { AssistantSettings } from "@/pages/Assistant";
import Admin from "@/pages/Admin";
import Integrations from "@/pages/Integrations";
import NotFound from "@/pages/NotFound";
import { AppShell } from "@/components/AppShell";

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#050505] text-white/50" data-testid="auth-loading">
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <BrowserRouter>
          <AuthProvider>
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/about" element={<About />} />
              <Route path="/promise" element={<PromisePage />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/widget/:agentId" element={<Widget />} />

              <Route
                path="/app"
                element={
                  <RequireAuth>
                    <SamTipsProvider>
                      <AppShell />
                    </SamTipsProvider>
                  </RequireAuth>
                }
              >
                <Route index element={<AppHome />} />
                <Route path="settings" element={<Settings />} />
                <Route path="chat" element={<Chat />} />
                <Route path="chat/:id" element={<Chat />} />
                <Route path="knowledge" element={<KnowledgeBase />} />
                <Route path="leads" element={<Leads />} />
                <Route path="sequences" element={<Sequences />} />
                <Route path="sequences/:id" element={<SequenceBuilder />} />
                <Route path="content" element={<Content />} />
                <Route path="agents" element={<Agents />} />
                <Route path="agents/:id" element={<AgentBuilder />} />
                <Route path="assistant" element={<Assistant />} />
                <Route path="assistant/settings" element={<AssistantSettings />} />
                <Route path="admin" element={<Admin />} />
                <Route path="integrations" element={<Integrations />} />
                <Route path="playbook" element={<Playbook />} />
                <Route path="playbook/:slug" element={<Playbook />} />
                <Route path=":slug" element={<ModuleStub />} />
              </Route>

              <Route path="*" element={<NotFound />} />
            </Routes>
            <Toaster position="top-right" />
          </AuthProvider>
        </BrowserRouter>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
